import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Info, Clock, Sparkles, CreditCard, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import PayPalCheckout from "../components/PayPalCheckout";
import PayPalProvider from "../components/PayPalProvider";
import { useMedia } from "../hooks/useMedia";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  { id: "small",  label: "Small",  sqft: "< 1,500 sq ft",  note: "Single storefront · corner retail · restaurant frontage" },
  { id: "medium", label: "Medium", sqft: "1,500 – 4,000 sq ft", note: "Mixed-use building · mid-block retail · medical office" },
  { id: "large",  label: "Large",  sqft: "4,000+ sq ft", note: "Full-block property · Class-A curtain wall · shopping plaza" },
];

// Map API service ids to the uploaded photography ids
const SERVICE_TO_IMAGE = {
  "pressure-washing": "pressure-washing",
  "awning-cleaning": "awning-cleaning",
  "window-cleaning": "window-cleaning",
  "window-cleaning-exterior": "window-cleaning",
  "window-cleaning-full": "window-cleaning",
  "sidewalk-cleaning": "sidewalk-cleaning",
  "entryway-detail": "sidewalk-cleaning",
  "parking-lot-striping": "parking-lot-striping",
  "parking-lot-sweeping": "parking-lot-sweeping",
  "snow-removal": "snow-removal",
  "snow-removal-event": "snow-removal",
  "graffiti-removal": "graffiti-removal",
  "dumpster-cleaning": "dumpster-cleaning",
  "outdoor-lighting": "outdoor-lighting",
  "outdoor-lighting-install": "outdoor-lighting",
  "pest-control": "pest-control",
  "sealcoat-striping": "parking-lot-striping",
};

export default function OneOff() {
  const [catalog, setCatalog] = useState(null);
  const [size, setSize] = useState("medium");
  const [checkingOut, setCheckingOut] = useState(null);
  const { url } = useMedia();

  useEffect(() => {
    axios.get(`${API}/one-off`).then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  async function checkout(serviceId) {
    const lookup = `bespoke-${serviceId}-${size}`;
    setCheckingOut(serviceId);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed — please try again.");
      setCheckingOut(null);
    }
  }

  if (!catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.4em]" style={{ background: "var(--bg)", color: "var(--text-3)" }}>
        Loading catalogue…
      </div>
    );
  }

  const activeSize = SIZES.find((s) => s.id === size);

  return (
    <PayPalProvider mode="capture">
    <div className="min-h-screen" data-testid="one-off-page">
      <PageHero
        eyebrow="À La Carte"
        title={<>Book one, <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>walk away.</span></>}
        subtitle="Not every property needs a monthly retainer yet. Book any single service by property size — three tiers, flat pricing, delivered within five business days."
        image={url("pressure-washing") || url("outdoor-lighting")}
        kicker="Ready when you are. No contract, no obligation."
      />

      {/* Notice + Size selector */}
      <section className="py-16" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div
            className="glass-card p-6 md:p-7 flex items-start gap-4 max-w-4xl"
            style={{ background: "rgba(198,168,124,0.06)", borderColor: "var(--line-gold)" }}
            data-testid="one-off-bundle-note"
          >
            <Sparkles size={17} className="mt-1 shrink-0" style={{ color: "var(--gold)" }} />
            <div className="font-body" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>
              Booking three or more services in one visit unlocks our bundled-visit discount. Considering a recurring cadence?{" "}
              <Link to="/pricing" className="underline underline-offset-4" style={{ color: "var(--gold)" }} data-testid="one-off-see-plans">See the monthly plans →</Link>
            </div>
          </div>

          {/* Size selector */}
          <div className="mt-14">
            <div className="overline mb-6">Select Your Property Size</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {SIZES.map((sz) => {
                const active = size === sz.id;
                return (
                  <button
                    key={sz.id}
                    onClick={() => setSize(sz.id)}
                    className="text-left p-7 transition-all duration-500"
                    style={{
                      borderRadius: "var(--r-lg)",
                      border: `1px solid ${active ? "var(--gold)" : "var(--line)"}`,
                      background: active ? "rgba(198,168,124,0.10)" : "rgba(11,11,13,0.5)",
                      color: "var(--text)",
                      boxShadow: active ? "0 0 24px rgba(198,168,124,0.18)" : "none",
                    }}
                    data-testid={`oneoff-size-${sz.id}`}
                    data-cursor="hover"
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-display text-3xl md:text-4xl" style={{ letterSpacing: "-0.025em" }}>{sz.label}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: active ? "var(--gold)" : "var(--text-3)" }}>{sz.sqft}</div>
                    </div>
                    <p className="mt-4 font-body text-sm" style={{ color: "var(--text-3)", lineHeight: 1.55 }}>{sz.note}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Service grid */}
      <section className="pb-24 md:pb-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="flex items-baseline justify-between mb-10">
            <div className="overline">Services · {activeSize.label} Property</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>{catalog.services.length} disciplines</div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {catalog.services.map((s, i) => {
              const price = s[size];
              const img = url(SERVICE_TO_IMAGE[s.id] || s.id);
              return (
                <motion.article
                  key={s.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.06 }}
                  className="glass-card overflow-hidden flex flex-col group"
                  data-testid={`oneoff-service-${s.id}`}
                  data-cursor="hover"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {img ? (
                      <img
                        src={img}
                        alt={s.label}
                        className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                        draggable={false}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.36em]" style={{ background: "rgba(198,168,124,0.05)", color: "var(--text-3)" }}>
                        {s.label}
                      </div>
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.85) 100%)" }} />
                    <div className="absolute top-5 left-6 pill" style={{ background: "rgba(11,11,13,0.72)" }}>
                      Service · {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
                      <div className="font-display text-2xl md:text-3xl leading-tight" style={{ letterSpacing: "-0.02em" }}>{s.label}</div>
                    </div>
                  </div>

                  <div className="p-7 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
                      <Clock size={12} strokeWidth={1.5} />
                      <span>Duration · {s.duration}</span>
                    </div>

                    <div className="mt-6 flex items-baseline gap-3">
                      {s.unit === "starting at" && (
                        <span className="font-mono text-[9px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>From</span>
                      )}
                      <span className="font-display leading-none" style={{ fontSize: "clamp(2.6rem, 4vw, 3.6rem)", color: "var(--gold-2)", letterSpacing: "-0.03em" }}>${price}</span>
                    </div>
                    <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
                      USD · {s.unit} · {activeSize.label} property
                    </div>

                    <button
                      onClick={() => checkout(s.id)}
                      disabled={checkingOut === s.id}
                      data-testid={`oneoff-book-${s.id}`}
                      data-cursor="hover"
                      data-cursor-label="Pay"
                      className="mt-8 inline-flex items-center justify-center gap-3 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.36em] transition-all duration-500 w-full"
                      style={{
                        borderRadius: "var(--r-full)",
                        background: checkingOut === s.id ? "rgba(198,168,124,0.14)" : "var(--gold)",
                        border: "1px solid var(--gold)",
                        color: checkingOut === s.id ? "var(--gold-2)" : "#0a0a0d",
                        opacity: checkingOut === s.id ? 0.75 : 1,
                        cursor: checkingOut === s.id ? "wait" : "pointer",
                        boxShadow: "0 0 24px rgba(198,168,124,0.25)",
                      }}
                    >
                      {checkingOut === s.id ? <><Loader2 size={13} className="animate-spin" /> Loading Stripe…</> : <><CreditCard size={13} /> Pay ${price} & Book</>}
                    </button>

                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1" style={{ height: 1, background: "var(--line)" }} />
                      <span className="font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>or</span>
                      <div className="flex-1" style={{ height: 1, background: "var(--line)" }} />
                    </div>

                    <div className="mt-3">
                      <PayPalCheckout
                        testId={`paypal-oneoff-${s.id}`}
                        orderPayload={{ lookup_key: `bespoke-${s.id}-${size}`, quantity: 1 }}
                        height={40}
                      />
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          <div
            className="mt-16 pt-10 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8"
            style={{ borderTop: "1px solid var(--line)" }}
          >
            <Info size={16} strokeWidth={1.4} style={{ color: "var(--gold)" }} className="shrink-0" />
            <p className="font-mono text-[10px] uppercase tracking-[0.36em] leading-[1.9]" style={{ color: "var(--text-3)" }}>
              One-off visits scheduled within 5 business days · Monthly retainer plans get priority booking + storm express re-clean · Custom scope? <Link to="/quote" className="underline underline-offset-4" style={{ color: "var(--gold)" }}>Request a quote →</Link>
            </p>
          </div>
        </div>
      </section>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          Prefer a locked-in <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>cadence?</span>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto px-6 font-body text-lg" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
          Retainer clients save 15–30% versus à-la-carte pricing, plus storm re-cleans and named crew leads.
        </p>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/pricing" className="btn-glow" data-cursor="hover" data-testid="oneoff-cta-plans">
            See Monthly Plans <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
    </PayPalProvider>
  );
}
